/**
 * ConfirmModal - Reusable confirmation dialog with blurred backdrop.
 *
 * A modal overlay that grays out and blurs the background, prevents
 * background scrolling, and allows its inner content to scroll when
 * taller than the viewport. Designed for confirmation dialogs, action
 * prompts, and any workflow that requires user acknowledgment.
 *
 * Features:
 *	 - Blurred + dimmed backdrop (backdrop-blur + semi-transparent overlay)
 *	 - Body scroll lock while open
 *	 - Scrollable inner content via max-height + overflow-y-auto
 *	 - Keyboard accessible: Escape key closes the modal
 *	 - Click-outside-to-close via backdrop click detection
 *	 - Configurable confirm/cancel button labels and styling
 *	 - Optional children for custom body content (form fields, etc.)
 *	 - Loading state disables buttons during async operations
 *
 * @example Simple confirmation
 * <ConfirmModal
 *	   isOpen={showConfirm}
 *	   onClose={() => setShowConfirm(false)}
 *	   onConfirm={handleDelete}
 *	   title="Deactivate User"
 *	   message="Are you sure you want to deactivate this user?"
 *	   confirmLabel="Deactivate"
 *	   variant="danger"
 * />
 *
 * @example With custom form content
 * <ConfirmModal
 *	   isOpen={showReset}
 *	   onClose={() => setShowReset(false)}
 *	   onConfirm={handleResetPassword}
 *	   title="Reset Password"
 *	   confirmLabel="Reset Password"
 *	   confirmDisabled={password.length < 12}
 * >
 *	   <input value={password} onChange={...} placeholder="New temporary password" />
 * </ConfirmModal>
 *
 * @example Three-way prompt (Cancel / secondary / confirm)
 * <ConfirmModal
 *	   isOpen={showSubmit}
 *	   onClose={() => setShowSubmit(false)}
 *	   onConfirm={() => submit(true)}
 *	   secondaryLabel="Don't Email"
 *	   onSecondary={() => submit(false)}
 *	   title="Submit for verification"
 *	   confirmLabel="Email & Submit"
 * />
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Visual style variant for the confirm button */
export type ConfirmModalVariant = 'primary' | 'danger';

/**
 * Props for the ConfirmModal component.
 *
 * @property isOpen			 - Controls visibility
 * @property onClose		 - Called when the user dismisses (Escape, backdrop, Cancel)
 * @property onConfirm		 - Called when the user clicks the confirm button
 * @property title			 - Header text
 * @property message		 - Optional body text (shown above children)
 * @property children		 - Optional custom body content (form fields, etc.)
 * @property confirmLabel	 - Confirm button text (default "Confirm")
 * @property cancelLabel	 - Cancel button text (default "Cancel")
 * @property secondaryLabel  - Optional middle button text (see onSecondary)
 * @property onSecondary	 - Optional handler for the middle button. Use it for
 *			   prompts with two ways to say yes — e.g. submit WITH
 *			   vs WITHOUT emailing the customer — where Cancel
 *			   still means "do nothing at all".
 * @property confirmDisabled - Disables the confirm button (e.g., form validation)
 * @property variant		 - Button color: 'primary' (brand orange) or 'danger' (red)
 * @property loading		 - Shows loading state on confirm button, disables both buttons
 * @property dataRef		 - Overrides the root `data-ref` marker. Pass the composing
 *			   component's own identity when ConfirmModal is used as a
 *			   shell (see NotifyUsersModal), so the rendered DOM names
 *			   the real component rather than the wrapper.
 */
export interface ConfirmModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: string;
	message?: string;
	children?: React.ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	secondaryLabel?: string;
	onSecondary?: () => void;
	confirmDisabled?: boolean;
	variant?: ConfirmModalVariant;
	loading?: boolean;
	/** Optional extra classes for the backdrop root */
	className?: string;
	/** Overrides the root `data-ref` marker — see the note above. */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const ConfirmModal: React.FC<ConfirmModalProps> = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	message,
	children,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	secondaryLabel,
	onSecondary,
	confirmDisabled = false,
	variant = 'primary',
	loading = false,
	className,
	dataRef = 'organism-confirm-modal',
}) => {
	/* Ref for the modal panel — used to detect outside clicks */
	const panelRef = useRef<HTMLDivElement>(null);

	/**
	 * Close modal on Escape key press.
	 */
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape' && !loading) {
				onClose();
			}
		},
		[onClose, loading],
	);

	/* Attach the Escape listener while the modal is open */
	useEffect(() => {
		if (!isOpen) return;
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, handleKeyDown]);

	/* Freeze the page behind the modal — the modal's inner content can
	   still scroll via overflow-y-auto. Stack-safe, so closing this
	   confirm over another Modal keeps the page locked. */
	useBodyScrollLock(isOpen);

	/**
	 * Close when clicking the backdrop (outside the panel).
	 * Clicks inside the panel are ignored so form fields work normally.
	 */
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (!loading && panelRef.current && !panelRef.current.contains(e.target as Node)) {
				onClose();
			}
		},
		[onClose, loading],
	);

	/* Don't render anything when closed */
	if (!isOpen) return null;

	/* Confirm button color based on variant */
	const confirmBtnClass = variant === 'danger'
		? 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white'
		: 'bg-brand-primary hover:bg-brand-primary-dark focus:ring-brand-primary text-white';

	return (
		/* Full-screen backdrop — semi-transparent gray with background blur.
		   The backdrop-blur-sm CSS filter blurs all content behind this overlay,
		   drawing visual focus to the modal panel. z-[70] layers above both the
		   BottomTabBar (z-50) and regular Modals (z-[60]) since confirms may
		   stack on top of an open form modal. */
		<div
			className={`confirm-modal fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-16px pb-[max(16px,env(safe-area-inset-bottom,0px))] ${className ?? ''}`}
			data-ref={dataRef}
			onClick={handleBackdropClick}
			role="dialog"
			aria-modal="true"
			aria-label={title}
		>
			{/* Modal panel — white card, capped to the backdrop's padded area so
			    inner content scrolls and the panel never extends off-screen */}
			<div
				ref={panelRef}
				className="confirm-modal__panel bg-white rounded-lg shadow-xl w-full max-w-md max-h-full flex flex-col"
			>
				{/* --- Header with title and close button --- */}
				<div className="confirm-modal__header flex items-center justify-between px-24px pt-24px pb-16px border-b border-brand-border shrink-0">
					<h3 className="text-18px font-bold font-heading text-brand-text">
						{title}
					</h3>
					<button
						type="button"
						onClick={onClose}
						disabled={loading}
						className="flex items-center justify-center w-44px h-44px -mr-8px text-brand-text-subtle hover:text-brand-text transition-colors rounded-md hover:bg-brand-surface"
						aria-label="Close modal"
					>
						<svg className="h-20px w-20px" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* --- Body — scrollable content area --- */}
				<div className="confirm-modal__body px-24px py-16px overflow-y-auto flex-1">
					{/* Optional message text */}
					{message && (
						<p className="text-14px text-brand-text-muted mb-12px">{message}</p>
					)}
					{/* Custom body content (form fields, etc.) */}
					{children}
				</div>

				{/* --- Footer with Cancel and Confirm buttons --- */}
				<div className="confirm-modal__footer flex flex-wrap justify-end gap-8px px-24px py-16px border-t border-brand-border shrink-0">
					<button
						type="button"
						onClick={onClose}
						disabled={loading}
						className="px-16px py-8px min-h-44px text-14px font-medium text-brand-text-muted hover:text-brand-text border border-brand-border rounded-md transition-colors"
					>
						{cancelLabel}
					</button>
					{/* Optional second way to say yes — outlined so it reads as a
						  real choice beside Cancel without competing with the
						  primary confirm. */}
					{secondaryLabel && onSecondary && (
						<button
							type="button"
							onClick={onSecondary}
							disabled={loading}
							className="px-16px py-8px min-h-44px text-14px font-medium text-brand-text border border-brand-border rounded-md hover:bg-brand-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{secondaryLabel}
						</button>
					)}
					<button
						type="button"
						onClick={onConfirm}
						disabled={confirmDisabled || loading}
						className={`px-16px py-8px min-h-44px text-14px font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${confirmBtnClass}`}
					>
						{loading ? 'Processing...' : confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ConfirmModal;
